// SPDX-License-Identifier: Elastic-2.0
/**
 * SwitchControls — single large toggle for a `switch` device.
 *
 * Per task spec § 6: "large `<Toggle size="lg">` only". Toggle (T-009) does
 * not expose a `size` prop today — the visual "large" treatment comes from
 * a wrapper class in `DeviceModal.module.css` that pumps the row's font /
 * spacing. The component itself is the thinnest possible adapter between
 * Device state and the Toggle primitive.
 *
 * Action contract: `turn_on` / `turn_off` (task spec § 2). Optimistic
 * state patch: `{ on: <next> }`.
 */
import { Toggle } from "@/components/Toggle";

import type { Device } from "@/api/gateway";

export interface SwitchControlsProps {
  device: Device;
  /**
   * Dispatched when the user toggles. The parent wires this into
   * `useControlDevice.mutate(...)` with the correct deviceId.
   */
  onAction: (
    action: string,
    params: Record<string, unknown>,
    optimisticStatePatch?: Record<string, unknown>,
  ) => void;
  disabled?: boolean;
}

/** Pull a boolean "on" out of any switch-like state envelope. */
function isOn(device: Device): boolean {
  const state = device.state ?? {};
  if (typeof state.on === "boolean") return state.on;
  if (typeof state.state === "string") {
    return state.state.toLowerCase() === "on";
  }
  return false;
}

export function SwitchControls({
  device,
  onAction,
  disabled,
}: SwitchControlsProps): JSX.Element {
  const on = isOn(device);

  function handleToggle(next: boolean): void {
    onAction(
      next ? "turn_on" : "turn_off",
      {},
      { on: next },
    );
  }

  return (
    <div data-testid="switch-controls">
      <Toggle
        checked={on}
        onChange={handleToggle}
        label={device.name}
        disabled={disabled}
        aria-label={`Toggle ${device.name}`}
      />
    </div>
  );
}

export default SwitchControls;
